import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePortfolioStats } from '../hooks/usePortfolioStats';
import StockDetailsModal from '../components/StockDetailsModal';
import FloatingAIAssistant from '../components/FloatingAIAssistant';
import FloatingCompetitionButton from '../components/FloatingCompetitionButton';
import MobileTopBarClean from '../components/MobileTopBarClean';
import { useTranslation } from 'react-i18next';
import '../styles/global-white-backgrounds.css';
import '../styles/professional-investment-cards.css';
import '../styles/stocks-page.css';
import { useIsMobile } from '../hooks/useIsMobile';

const CryptoETFPage = () => {
  const navigate = useNavigate();
  const { getHeaderStats } = usePortfolioStats();
  const { t } = useTranslation();
  const [cryptoETFs, setCryptoETFs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCrypto, setSelectedCrypto] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const isMobile = useIsMobile();
  const [draggedCrypto, setDraggedCrypto] = useState(null);
  const [touchStartPos, setTouchStartPos] = useState(null);
  const [touchMoved, setTouchMoved] = useState(false);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isOverDropZone, setIsOverDropZone] = useState(false);
  const dragElementRefs = useState(() => new Map())[0];

  useEffect(() => {
    fetchCryptoETFs();
  }, []);


  // Setup non-passive touch events for mobile drag
  useEffect(() => {
    if (!isMobile) return;

    const handleTouchMove = (e) => {
      if (!touchStartPos || !draggedCrypto) return;
      
      const currentX = e.touches[0].clientX;
      const currentY = e.touches[0].clientY;
      const deltaX = Math.abs(currentX - touchStartPos.x);
      const deltaY = Math.abs(currentY - touchStartPos.y);
      
      // Very low threshold for easy mobile dragging
      if (deltaX > 5 || deltaY > 5) {
        if (!touchMoved) {
          setTouchMoved(true);
          setIsDragging(true);
        }
        
        // Update drag position for visual feedback
        const dragX = currentX - touchStartPos.x;
        const dragY = currentY - touchStartPos.y;
        setDragPosition({ x: dragX, y: dragY });
        
        // Check if dragging over drop zone for visual feedback
        const elementAtPoint = document.elementFromPoint(currentX, currentY);
        const overDropZone = elementAtPoint?.closest('.icon-container') || 
                           elementAtPoint?.closest('.floating-ai-assistant');
        setIsOverDropZone(!!overDropZone);
        
        // Add haptic feedback on iOS when over drop zone
        if (overDropZone && navigator.vibrate) {
          navigator.vibrate(10);
        }
        
        e.preventDefault(); // This will now work with non-passive events
        e.stopPropagation();
      }
    };

    // Add non-passive touch event listeners to all draggable elements
    dragElementRefs.forEach((element) => {
      if (element) {
        element.addEventListener('touchmove', handleTouchMove, { passive: false });
      }
    });

    return () => {
      dragElementRefs.forEach((element) => {
        if (element) {
          element.removeEventListener('touchmove', handleTouchMove);
        }
      });
    };
  }, [isMobile, touchStartPos, draggedCrypto, touchMoved, dragElementRefs]);

  const fetchCryptoETFs = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Bitcoin and Ethereum ETF tickers
      const cryptoTickers = ['BITO', 'ETHE']; // Bitcoin Strategy ETF and Ethereum Trust
      
      const cryptoETFsWithDetails = await Promise.all(
        cryptoTickers.map(async (ticker) => {
          try {
            // Get detailed company info
            const detailResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/tickers/${ticker}`);
            const detailData = await detailResponse.json();
            
            // Get current price data 
            const priceResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/tickers/${ticker}/prev`);
            const priceData = await priceResponse.json();
            
            const companyDetails = detailData.results || {};
            const currentData = priceData.results?.[0] || {};
            
            return {
              ticker: ticker,
              name: companyDetails.name || ticker,
              currentPrice: currentData.c || 0,
              close: currentData.c || 0, // StockDetailsModal expects 'close'
              ask: currentData.h || 0, // Day high as ask
              bid: currentData.l || 0, // Day low as bid
              change: 0, // Will be calculated properly later
              changePercent: 0,
              dayHigh: currentData.h || 0,
              dayLow: currentData.l || 0,
              volume: currentData.v || 0,
              marketCap: companyDetails.market_cap || 0,
              logoUrl: `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/logos/${ticker}/logo`,
              iconUrl: `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/logos/${ticker}/icon`
            };
          } catch (error) {
            console.warn(`Failed to enhance data for ${ticker}:`, error);
            return null;
          }
        })
      );
      
      // Filter out failed requests
      const validCryptoETFs = cryptoETFsWithDetails.filter(crypto => crypto);
      
      // If we don't have data for the specific tickers, try to find crypto ETFs through search
      if (validCryptoETFs.length === 0) {
        console.warn('No crypto ETF data found for specific tickers, trying search...');
        
        // Fallback: Try to get any crypto-related ETFs
        const fallbackResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/tickers?market=stocks&type=ETF&active=true&limit=50`);
        const fallbackData = await fallbackResponse.json();
        
        if (fallbackData.status === 'OK' && fallbackData.results) {
          // Filter for crypto-related ETFs based on name
          const cryptoKeywords = ['bitcoin', 'crypto', 'ethereum', 'blockchain', 'digital'];
          const potentialCryptoETFs = fallbackData.results.filter(etf => 
            cryptoKeywords.some(keyword => 
              etf.name?.toLowerCase().includes(keyword) || 
              etf.ticker?.toLowerCase().includes(keyword)
            )
          ).slice(0, 2); // Take only first 2
          
          const fallbackETFs = await Promise.all(
            potentialCryptoETFs.map(async (etf) => {
              try {
                const priceResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/tickers/${etf.ticker}/prev`);
                const priceData = await priceResponse.json();
                const currentData = priceData.results?.[0] || {};
                
                return {
                  ticker: etf.ticker,
                  name: etf.name || etf.ticker,
                  currentPrice: currentData.c || 0,
                  close: currentData.c || 0,
                  ask: currentData.h || 0,
                  bid: currentData.l || 0,
                  change: 0,
                  changePercent: 0,
                  dayHigh: currentData.h || 0,
                  dayLow: currentData.l || 0,
                  volume: currentData.v || 0,
                  marketCap: 0,
                  logoUrl: `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/logos/${etf.ticker}/logo`,
                  iconUrl: `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/logos/${etf.ticker}/icon`
                };
              } catch (error) {
                console.warn(`Failed to fetch fallback data for ${etf.ticker}:`, error);
                return null;
              }
            })
          );
          
          setCryptoETFs(fallbackETFs.filter(crypto => crypto));
        } else {
          // If all else fails, create mock data for demo purposes
          setCryptoETFs([
            {
              ticker: 'BITO',
              name: 'ProShares Bitcoin Strategy ETF',
              currentPrice: 25.50,
              close: 25.50,
              ask: 25.55,
              bid: 25.45,
              change: 0.25,
              changePercent: 0.98,
              dayHigh: 25.80,
              dayLow: 25.20,
              volume: 1250000,
              marketCap: 0,
              logoUrl: `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/logos/BITO/logo`,
              iconUrl: `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/logos/BITO/icon`
            },
            {
              ticker: 'ETHE',
              name: 'Grayscale Ethereum Trust',
              currentPrice: 18.25,
              close: 18.25,
              ask: 18.30,
              bid: 18.20,
              change: -0.15,
              changePercent: -0.82,
              dayHigh: 18.45,
              dayLow: 18.10,
              volume: 850000,
              marketCap: 0,
              logoUrl: `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/logos/ETHE/logo`,
              iconUrl: `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/logos/ETHE/icon`
            }
          ]);
        }
      } else {
        setCryptoETFs(validCryptoETFs);
      }
      
      setError(null);
    } catch (error) {
      console.error('Error fetching crypto ETFs:', error);
      setError('Failed to load crypto ETF data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Handle crypto ETF click - open stock details modal
  const handleCryptoClick = (crypto) => {
    setSelectedCrypto(crypto);
    setModalOpen(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedCrypto(null);
  };

  return (
    <div className="dashboard-container jpx-style">
      <div className="main-content" style={{ marginLeft: 0, width: '100%' }}>
        {/* X BUTTON TO RETURN TO DASHBOARD */}
        <button className="modal-close" onClick={() => navigate('/dashboard')} style={{
          position: 'fixed',
          top: '120px',
          right: '20px',
          width: '40px',
          height: '40px',
          background: '#000000',
          color: '#ffffff',
          border: 'none',
          borderRadius: '8px',
          fontSize: '24px',
          cursor: 'pointer',
          zIndex: 1001,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.3s ease'
        }}>×</button>
        {/* Conditional Header - Mobile vs Desktop */}
        <MobileTopBarClean stats={getHeaderStats()} />


        {/* Main content */}
        <main className="dashboard-content" style={{
          marginTop: isMobile ? '75px' : '0'
        }}>
          {/* Error Display */}
          {error && (
            <div className="error-message">
              <span className="error-icon"></span>
              {error}
            </div>
          )}

          {/* Crypto ETF Grid */}
          <section className="stocks-section">
            <div className="section-header">
              <h2 className="section-title">{t('assets:pages.cryptoEtf.sectionTitle')}</h2>
              <div className="section-subtitle">{t('assets:pages.cryptoEtf.sectionSubtitle')}</div>
            </div>

            {loading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>{t('assets:loading.cryptoEtfData')}</p>
              </div>
            ) : (
              <div className="stocks-grid">
                {cryptoETFs.map((crypto) => (
                  <div 
                    key={crypto.ticker}
                    className="stock-card stocks"
                    onClick={(e) => {
                      // Don't trigger click if we're dragging
                      if (!touchMoved && !draggedCrypto) {
                        handleCryptoClick(crypto);
                      }
                    }}
                  >
                    {/* Crypto Logo - Draggable */}
                    <div 
                      ref={(el) => {
                        if (el) {
                          dragElementRefs.set(crypto.ticker, el);
                        }
                      }}
                      className={`stock-logo draggable-logo ${
                        draggedCrypto === crypto.ticker ? 'dragging-active' : ''
                      } ${isDragging && draggedCrypto === crypto.ticker ? 'dragging-move' : ''}`}
                      draggable="true"
                      onDragStart={(e) => {
                        e.stopPropagation();
                        e.dataTransfer.setData('application/json', JSON.stringify({
                          ticker: crypto.ticker,
                          name: crypto.name,
                          price: crypto.close,
                          type: 'crypto-etf'
                        }));
                        e.dataTransfer.effectAllowed = 'copy';
                        setDraggedCrypto(crypto.ticker);
                      }}
                      onDragEnd={(e) => {
                        setDraggedCrypto(null);
                      }}
                      // Mobile touch support
                      onTouchStart={(e) => {
                        e.stopPropagation();
                        const touch = e.touches[0];
                        setTouchStartPos({
                          x: touch.clientX,
                          y: touch.clientY,
                          time: Date.now(),
                          pageX: touch.pageX,
                          pageY: touch.pageY
                        });
                        setTouchMoved(false);
                        setIsDragging(false);
                        setDragPosition({ x: 0, y: 0 });
                        
                        // Provide immediate visual feedback
                        setDraggedCrypto(crypto.ticker);
                      }}
                      onTouchMove={(e) => {
                        // This is now handled by the non-passive event listener in useEffect
                        if (touchStartPos && draggedCrypto === crypto.ticker) {
                          e.stopPropagation();
                        }
                      }}
                      onTouchEnd={(e) => {
                        e.stopPropagation();
                        
                        if (!touchStartPos) {
                          return;
                        }
                        
                        // Only trigger if user dragged to a drop zone
                        if (touchMoved) {
                          // Check if touch ended over the AI assistant drop zone
                          const touchEndX = e.changedTouches[0].clientX;
                          const touchEndY = e.changedTouches[0].clientY;
                          const elementAtEnd = document.elementFromPoint(touchEndX, touchEndY);
                          
                          // Look for the AI assistant drop zone or its children
                          const dropZone = elementAtEnd?.closest('.icon-container') || 
                                          elementAtEnd?.closest('.floating-ai-assistant');
                          
                          if (dropZone) {
                            console.log('✅ MOBILE DRAG: Dispatching event for', crypto.ticker);
                            const event = new CustomEvent('tickerDropped', {
                              detail: {
                                ticker: crypto.ticker,
                                name: crypto.name,
                                price: crypto.close,
                                type: 'crypto-etf'
                              }
                            });
                            window.dispatchEvent(event);
                            console.log('✅ MOBILE DRAG: Event dispatched successfully');
                          } else {
                            console.log('❌ MOBILE DRAG: Not over drop zone. Element:', elementAtEnd?.className);
                          }
                        }
                        
                        // Reset all touch state
                        setTouchStartPos(null);
                        setTouchMoved(false);
                        setDraggedCrypto(null);
                        setIsDragging(false);
                        setDragPosition({ x: 0, y: 0 });
                        setIsOverDropZone(false);
                      }}
                      style={{ 
                        cursor: 'grab',
                        opacity: draggedCrypto === crypto.ticker ? (isDragging ? 0.8 : 0.9) : 1,
                        '--drag-x': isDragging && draggedCrypto === crypto.ticker ? `${dragPosition.x}px` : '0px',
                        '--drag-y': isDragging && draggedCrypto === crypto.ticker ? `${dragPosition.y}px` : '0px',
                        '--drag-scale': draggedCrypto === crypto.ticker ? 
                          (isDragging ? (isOverDropZone ? '1.25' : '1.15') : '1.05') : '1',
                        '--drag-rotate': isDragging && draggedCrypto === crypto.ticker ? '2deg' : '0deg',
                        boxShadow: draggedCrypto === crypto.ticker ? '0 8px 20px rgba(0,0,0,0.3)' : 'none',
                        transition: isDragging ? 'none' : 'all 0.2s ease',
                        touchAction: 'none',
                        WebkitUserDrag: 'element',
                        userSelect: 'none',
                        backgroundColor: draggedCrypto === crypto.ticker ? 
                          (isOverDropZone ? '#e0f2fe' : '#f0f0f0') : 'transparent',
                        borderRadius: '8px',
                        zIndex: draggedCrypto === crypto.ticker ? 1000 : 1
                      }}
                      title={isMobile ? t('assets:card.mobileDragHintCrypto') : t('assets:card.dragHintCrypto')}
                    >
                      <img 
                        src={crypto.logoUrl}
                        alt={`${crypto.name} logo`}
                        style={{ 
                          pointerEvents: 'none',
                          touchAction: 'none',
                          userSelect: 'none',
                          WebkitUserSelect: 'none',
                          WebkitTouchCallout: 'none'
                        }}
                        onError={(e) => {
                          // First fallback: try icon instead of logo
                          if (e.target.src === crypto.logoUrl) {
                            e.target.src = crypto.iconUrl;
                          } else {
                            // Final fallback: placeholder with ticker letter
                            const bgColor = crypto.ticker.includes('BIT') || crypto.ticker.includes('BTC') ? '%23f7931a' : '%236c5ce7';
                            e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><rect width="100" height="100" fill="' + bgColor + '" rx="16"/><text x="50" y="60" font-family="Arial, sans-serif" font-size="36" font-weight="bold" fill="white" text-anchor="middle">' + crypto.ticker[0] + '</text></svg>';
                          }
                        }}
                      />
                    </div>

                    {/* Crypto Info */}
                    <div className="stock-info">
                      <div className="stock-header">
                        <h3 className="stock-ticker">{crypto.ticker}</h3>
                        <div className="stock-name">{crypto.name}</div>
                      </div>

                      <div className="stock-price">
                        <span className="current-price">${crypto.currentPrice?.toFixed(2) || 'N/A'}</span>
                        <div className={`price-change ${crypto.change >= 0 ? 'positive' : 'negative'}`}>
                          {crypto.change >= 0 ? '+' : ''}{crypto.change?.toFixed(2) || 'N/A'} 
                          ({crypto.changePercent >= 0 ? '+' : ''}{crypto.changePercent?.toFixed(2) || 'N/A'}%)
                        </div>
                      </div>

                      <div className="stock-metrics">
                        <div className="metric">
                          <span className="metric-label">Day High</span>
                          <span className="metric-value">${crypto.dayHigh?.toFixed(2) || 'N/A'}</span>
                        </div>
                        <div className="metric">
                          <span className="metric-label">Day Low</span>
                          <span className="metric-value">${crypto.dayLow?.toFixed(2) || 'N/A'}</span>
                        </div>
                        <div className="metric">
                          <span className="metric-label">Volume</span>
                          <span className="metric-value">{crypto.volume?.toLocaleString() || 'N/A'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Action Button */}
                    <button
                      className="stock-action"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCryptoClick(crypto);
                      }}
                    >
                      {t('assets:card.actions.viewDetails')}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {!loading && cryptoETFs.length === 0 && !error && (
              <div className="empty-state">
                <div className="empty-icon">₿</div>
                <h3>{t('assets:errors.noCryptoEtfsFound')}</h3>
                <p>{t('assets:errors.cryptoDataSoon')}</p>
              </div>
            )}
          </section>
        </main>

        {/* Stock Details Modal */}
        {modalOpen && selectedCrypto && (
          <StockDetailsModal 
            stock={selectedCrypto}
            isOpen={modalOpen}
            onClose={handleCloseModal}
          />
        )}

        {/* Floating AI Assistant */}
        <FloatingAIAssistant />

        {/* Floating Competition Button */}
        <FloatingCompetitionButton />
      </div>
    </div>
  );
};

export default CryptoETFPage;